import React, { useState } from 'react';
import { LogOut } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import { darSaida, MOTIVOS_DE_SAIDA, MOTIVO_POR_ID, enderecoDoBotijao } from '../../../data/estoque';
import { MARK } from '../../../constants';

const input = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const Campo = ({ label, children, dica }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    {children}
    {dica && <p className="text-xs text-slate-400 mt-0.5">{dica}</p>}
  </div>
);

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR',
  { style: 'currency', currency: 'BRL' });

/**
 * Tira embriões do botijão sem ser por transferência.
 *
 * Venda, devolução, envio a outro laboratório, descarte, perda. A saída é
 * definitiva: o embrião deixa o estoque e passa a aparecer no relatório de
 * saídas — não há como devolvê-lo pela tela, e é por isso que a confirmação
 * lista o que vai sair.
 */
const SaidaDeEmbrioes = ({ selecionados = [], onPronto, onFechar }) => {
  const [f, setF] = useState({
    motivo: 'venda',
    saida_em: new Date().toISOString().slice(0, 10),
    destino: '', valorTotal: '', retirado_por: '', observacao: '',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const qtd = selecionados.length;
  const pedeValor = MOTIVO_POR_ID[f.motivo]?.pedeValor === true;
  const total = Number(f.valorTotal) || 0;
  const pode = qtd > 0 && f.motivo && f.saida_em && !salvando;

  const gravar = async () => {
    setSalvando(true); setErro('');
    try {
      await darSaida(selecionados.map(e => e.embryo_id), {
        motivo: f.motivo,
        saida_em: f.saida_em,
        destino: f.destino,
        valorTotal: pedeValor ? total : 0,
        retirado_por: f.retirado_por,
        observacao: f.observacao,
      });
      onPronto?.();
      onFechar?.();
    } catch (e) {
      console.error('saída de embriões:', e);
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <JanelaModal chave="saida-embrioes" Icone={LogOut} titulo={<>Dar saída — {qtd} embriõ{qtd > 1 ? 'es' : ''}</>} onFechar={onFechar} largura="sm:max-w-2xl">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* O que vai sair, escrito. A ação é definitiva e a seleção foi feita
              numa lista que rolou — conferir aqui é a última chance. */}
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <p className="text-xs font-medium text-slate-700 mb-1">Saem do botijão</p>
            <ul className="text-xs text-slate-600 space-y-0.5">
              {selecionados.map(e => (
                <li key={e.embryo_id}>
                  {e.numero != null ? `nº ${e.numero} · ` : ''}
                  {e.doadora || 'doadora não informada'}
                  {e.touro ? ` × ${e.touro}` : ''}
                  {e.client_name ? ` — ${e.client_name}` : ''}
                  {enderecoDoBotijao(e) ? ` (${enderecoDoBotijao(e)})` : ''}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Campo label="Motivo *">
              <select className={input} value={f.motivo} onChange={(e) => set('motivo', e.target.value)}>
                {MOTIVOS_DE_SAIDA.map(m => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </Campo>
            <Campo label="Data da saída *">
              <input className={input} type="date" value={f.saida_em}
                onChange={(e) => set('saida_em', e.target.value)} />
            </Campo>
            <Campo label="Quem retirou">
              <input className={input} value={f.retirado_por}
                onChange={(e) => set('retirado_por', e.target.value)} />
            </Campo>
          </div>

          <Campo label="Para quem foi"
            dica="Cliente, laboratório ou fazenda que recebeu">
            <input className={input} value={f.destino}
              onChange={(e) => set('destino', e.target.value)} />
          </Campo>

          {/* O valor só aparece na venda. Campo de dinheiro ao lado de
              "descarte" convida a preencher, e aí o relatório passa a somar
              receita de embrião jogado fora. */}
          {pedeValor && (
            <Campo label="Valor total da venda"
              dica={qtd > 1 && total > 0
                ? `Dividido por embrião: ${dinheiro(total / qtd)} cada`
                : 'Do lote inteiro — a divisão por embrião é feita na gravação'}>
              <input className={input} type="number" step="0.01" min="0" value={f.valorTotal}
                onChange={(e) => set('valorTotal', e.target.value)} />
            </Campo>
          )}

          <Campo label="Observação">
            <input className={input} value={f.observacao}
              onChange={(e) => set('observacao', e.target.value)} />
          </Campo>

          <p className="text-xs text-slate-600">
            {MARK.WARN} A saída é definitiva. {qtd > 1 ? 'Estes embriões saem' : 'Este embrião sai'} do
            estoque e {qtd > 1 ? 'passam' : 'passa'} a aparecer no relatório de saídas.
          </p>

          {erro && (
            <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded px-3 py-2">
              {erro}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onFechar}
            className="px-4 py-2 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
            Cancelar
          </button>
          <button onClick={gravar} disabled={!pode}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40">
            {salvando ? 'Gravando…' : 'Confirmar saída'}
          </button>
        </div>
    </JanelaModal>
  );
};

export default SaidaDeEmbrioes;
